import { isReduceMotionEnabled } from './reduce-motion';

/**
 * Looping video backdrop with a still poster underneath.
 *
 * Extracted from profile-screen.ts, which had the only one. Now that Battle,
 * Quests and Shop want the same treatment, the three constraints that make it
 * work are worth stating once rather than re-deriving per screen:
 *
 *  1. The poster renders the screen completely on its own. The video is pure
 *     enhancement and fades in only on `canplay`, so a missing or slow asset
 *     leaves a finished-looking background rather than flashing black.
 *  2. Reduce-motion is handled HERE, in JS, by never creating the <video> at
 *     all. CSS cannot pause a video, so a CSS-only approach would make the
 *     toggle a lie.
 *  3. `frontend/server.js` must serve .webm and .mp4 with correct MIME types.
 *     Vite's dev server does it automatically; the production static server
 *     does not, which makes this the kind of bug that only appears after
 *     deploy.
 */

/** Above this CSS width the 2k poster earns its bytes — mirrors engine/colony-background.ts. */
const POSTER_SWITCH_WIDTH_PX = 1024;

export interface AmbientSources {
  /** Poster shown immediately and whenever the video is suppressed. */
  poster1k: string;
  poster2k: string;
  webm: string;
  mp4: string;
  /** When false the video loops natively with no opacity crossfade at the
   *  seam. Use for clips whose start/end frames are narrative (e.g. a VTOL
   *  flying in then away) rather than ambient. Defaults to true. */
  crossfade?: boolean;
}

/** Every screen that has an ambient backdrop, with its asset set. */
export const AMBIENT: Record<'profile' | 'battle' | 'quests' | 'shop' | 'auth', AmbientSources> = {
  auth: {
    poster1k: '/auth/auth-bg-1k.webp',
    poster2k: '/auth/auth-bg-2k.webp',
    webm: '/auth/auth-bg.webm',
    mp4: '/auth/auth-bg.mp4',
  },
  profile: {
    poster1k: '/profile/hab-viewport-1k.webp',
    poster2k: '/profile/hab-viewport-2k.webp',
    webm: '/profile/hab-viewport.webm',
    mp4: '/profile/hab-viewport.mp4',
  },
  battle: {
    poster1k: '/ambient/battle-1k.webp',
    poster2k: '/ambient/battle-2k.webp',
    webm: '/ambient/battle.webm',
    mp4: '/ambient/battle.mp4',
  },
  quests: {
    poster1k: '/ambient/quests-1k.webp',
    poster2k: '/ambient/quests-2k.webp',
    webm: '/ambient/quests.webm',
    mp4: '/ambient/quests.mp4',
    crossfade: false,
  },
  shop: {
    poster1k: '/ambient/shop-1k.webp',
    poster2k: '/ambient/shop-2k.webp',
    webm: '/ambient/shop.webm',
    mp4: '/ambient/shop.mp4',
  },
};

/** True when motion should be suppressed, by the in-app toggle or the OS setting. */
export function prefersStillBackground(): boolean {
  return (
    isReduceMotionEnabled() ||
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
  );
}

/**
 * Fills `el` with the backdrop. `el` must already be positioned (the markup
 * below is absolutely positioned within it).
 *
 * Call on mount and again on remount; there is no subscription, because
 * switching tabs remounts the screen and so picks up a changed toggle anyway.
 */
export function mountAmbientBackground(el: HTMLElement, sources: AmbientSources): void {
  const poster = window.innerWidth >= POSTER_SWITCH_WIDTH_PX ? sources.poster2k : sources.poster1k;
  const stillOnly = prefersStillBackground();

  el.innerHTML = `
    <div class="absolute inset-0 bg-cover bg-center" style="background-image:url('${poster}')"></div>
    ${
      stillOnly
        ? ''
        : `<video data-ambient-video autoplay muted loop playsinline preload="metadata" poster="${poster}"
             class="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-700">
             <source src="${sources.webm}" type="video/webm">
             <source src="${sources.mp4}" type="video/mp4">
           </video>`
    }
    <div class="absolute inset-0 ambient-scrim"></div>
  `;

  const video = el.querySelector('[data-ambient-video]') as HTMLVideoElement | null;
  video?.addEventListener('canplay', () => video.classList.remove('opacity-0'), { once: true });

  if (video && sources.crossfade !== false) {
    const FADE_OUT_S = 0.35;
    let fading = false;
    video.addEventListener('timeupdate', () => {
      if (!video.duration || video.duration < FADE_OUT_S * 3) return;
      if (!fading && video.currentTime >= video.duration - FADE_OUT_S) {
        fading = true;
        video.style.transition = `opacity ${FADE_OUT_S}s ease-out`;
        video.style.opacity = '0';
      }
    });
    video.addEventListener('seeked', () => {
      if (fading && video.currentTime < 1) {
        fading = false;
        video.style.transition = 'none';
        video.style.opacity = '1';
        requestAnimationFrame(() => {
          video.style.transition = '';
        });
      }
    });
  }
}
